<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-switch
          :model-value="Boolean(section.camera_info)"
          label="Activer la section Caméra"
          :loading="saving"
          @update:model-value="save({ camera_info: $event })"
        ></v-switch>
        <small class="text-medium-emphasis">(Les changements de cet onglet sont sauvegardés automatiquement)</small>
      </v-col>
      <v-col cols="12" md="6">
        <v-autocomplete
          :model-value="section.camera_id || null"
          :items="cameras"
          item-title="name"
          item-value="id"
          label="Caméra Spypoint"
          :loading="loadingCameras"
          :no-data-text="'Aucune caméra : configurez le compte dans la page Caméras.'"
          clearable
          @update:model-value="save({ camera_id: $event || '' })"
        >
          <!-- Vuetify 4 passes the camera itself as `item` (the wrapped item is `internalItem`) -->
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :subtitle="[item.model, item.owner_name].filter(Boolean).join(' · ')"
            ></v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-4">
      {{ errorMessage }}
    </v-alert>
    <v-alert v-if="section.camera_info && !section.camera_id" type="info" variant="tonal" density="compact" class="mb-4">
      Choisissez une caméra : l'onglet Caméra reste caché tant qu'aucune caméra n'est choisie.
    </v-alert>

    <template v-if="camera">
      <v-alert v-if="camera.error" type="error" variant="tonal" density="compact" class="mb-4">
        {{ camera.error }}
      </v-alert>
      <div class="d-flex align-center flex-wrap ga-2 mb-3">
        <h3 class="text-title-medium font-weight-bold">Photos actives</h3>
        <span class="text-body-small text-medium-emphasis">
          Dernière extraction : {{ camera.last_extract_at ? formatDate(camera.last_extract_at) : "jamais" }}
        </span>
      </div>
      <v-row v-if="photos.length" dense>
        <v-col v-for="photo in photos" :key="photo.id" cols="6" sm="4" md="2">
          <a :href="photo.url" target="_blank" rel="noopener noreferrer" class="camera-photo">
            <img :src="photo.url" :alt="`Photo du ${formatDate(photo.taken_at)}`" loading="lazy" />
          </a>
          <div class="text-body-small text-center mt-1">{{ formatDate(photo.taken_at) }}</div>
        </v-col>
      </v-row>
      <p v-else class="text-body-medium text-medium-emphasis">Aucune photo pour cette caméra.</p>
    </template>
  </v-container>
</template>

<script>
  import { CamerasService } from "@/services/Cameras.service";
  import { notify, notifyError } from "@/services/Notification.service";
  import { formatPhotoDate } from "@/utils/cameras";

  export default {
    name: "SectionCamera",
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
    },
    data() {
      return {
        cameras: [],
        loadingCameras: true,
        saving: false,
        errorMessage: "",
      };
    },
    computed: {
      camera() {
        return this.cameras.find((camera) => camera.id === this.section.camera_id) || null;
      },
      photos() {
        return (this.camera && this.camera.photos) || [];
      },
    },
    async created() {
      try {
        this.cameras = await CamerasService.listCameras();
      } catch (error) {
        console.error("Error loading cameras:", error);
        this.errorMessage = "Les caméras n'ont pas pu être chargées.";
      } finally {
        this.loadingCameras = false;
      }
    },
    methods: {
      formatDate: formatPhotoDate,
      async save(changes) {
        const values = {
          camera_info: Boolean(this.section.camera_info),
          camera_id: this.section.camera_id || "",
          ...changes,
        };
        this.saving = true;
        this.errorMessage = "";
        try {
          await CamerasService.setSectionCamera(this.section.id, values);
          Object.assign(this.section, values);
          notify("La caméra de la section a été sauvegardée.");
        } catch (error) {
          console.error(error);
          notifyError("La caméra de la section n'a pas pu être sauvegardée.");
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>

<style scoped>
  .camera-photo {
    display: block;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface-variant));
  }

  .camera-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
</style>
